/**
 * @module NotificationPreferencesPage
 * @package @geenius/notifications-react
 * @description Renders the full-page notification preference settings screen
 * for the Tailwind React variant.
 */

import { FadeIn } from '@geenius/motion/react'
import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  DEFAULT_CHANNEL_PREFS,
  DEFAULT_TYPE_PREFS,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/react'
import type { JSX } from 'react'
import { NotificationPreferencesForm } from '../components/NotificationPreferencesForm'
import { useNotificationPreferences } from '../hooks/useNotificationPreferences'
import { useNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Props for the React notification preferences page.
 */
export interface NotificationPreferencesPageProps {
  preferences: NotificationPreferences | null | undefined
  onUpdate?: (
    channels?: Record<NotificationChannel, boolean>,
    types?: Record<NotificationType, boolean>,
    quietHours?: { start: string; end: string },
  ) => Promise<void>
  className?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']
const TYPES: NotificationType[] = [
  'info',
  'success',
  'warning',
  'error',
  'mention',
  'invite',
  'payment',
  'system',
]
const PREFERENCE_SKELETON_KEYS = [
  'notification-preferences-skeleton-1',
  'notification-preferences-skeleton-2',
  'notification-preferences-skeleton-3',
  'notification-preferences-skeleton-4',
  'notification-preferences-skeleton-5',
  'notification-preferences-skeleton-6',
  'notification-preferences-skeleton-7',
  'notification-preferences-skeleton-8',
]

/**
 * Renders the full-page notification preferences experience.
 *
 * @param props Page props containing current preferences and update callback.
 * @returns The preferences page markup.
 */
export function NotificationPreferencesPage({
  preferences: rawPreferences,
  onUpdate,
  className,
}: NotificationPreferencesPageProps): JSX.Element {
  const { t } = useNotificationCopy()
  const { preferences, isLoading, toggleChannel, toggleType, setQuietHours } =
    useNotificationPreferences(rawPreferences, onUpdate)

  if (isLoading) {
    return (
      <div className={cn('mx-auto max-w-2xl p-6', className)}>
        {PREFERENCE_SKELETON_KEYS.map((skeletonKey) => (
          <div
            key={skeletonKey}
            className="mb-2 h-10 animate-pulse rounded-lg bg-[var(--gn-notifications-skeleton)]"
          />
        ))}
      </div>
    )
  }

  const resolvedPreferences = preferences ?? {
    userId: '',
    channels: { ...DEFAULT_CHANNEL_PREFS },
    types: { ...DEFAULT_TYPE_PREFS },
    quietHours: { start: '22:00', end: '07:00' },
  }

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.preferencesTitle'),
    description: t('notifications.seo.preferencesDescription'),
    path: '/notifications/preferences',
    keywords: ['notifications', 'preferences', 'channels', 'quiet hours'],
  })

  return (
    <>
      <SEOHead meta={seoMeta} />
      <section className={cn('mx-auto max-w-2xl space-y-6 p-6', className)}>
        <FadeIn>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('notifications.preferences.pageTitle')}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {t('notifications.preferences.pageDescription')}
            </p>
          </div>
        </FadeIn>
        <FadeIn>
          <NotificationPreferencesForm
            preferences={resolvedPreferences}
            onUpdate={async (channels, types, quietHours) => {
              if (channels) {
                for (const channel of CHANNELS) {
                  if (resolvedPreferences.channels[channel] !== channels[channel]) {
                    await toggleChannel(channel, channels[channel])
                  }
                }
              }

              if (types) {
                for (const type of TYPES) {
                  if (resolvedPreferences.types[type] !== types[type]) {
                    await toggleType(type, types[type])
                  }
                }
              }

              if (quietHours) {
                await setQuietHours(quietHours.start, quietHours.end)
              }
            }}
          />
        </FadeIn>
      </section>
    </>
  )
}
